// src/components/pages/SignInPage.js

import React, { useState, useEffect } from "react";
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useTheme } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import AuthComponent from "../molecules/AuthComponent";
import { fetchCustomToken } from "../../utils/azureUtils";
import useStore from "../../store/useStore";
import useAuthStore from "../../hooks/useAuthStore";
import useUserProfile from "../../hooks/useUserProfile";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import {
  fetchUserProfileFromFirestore,
  forgotPassword,
} from "../../services/profileService";

// Hoisted constant → no dependency warning
const EVENTS_PATH_REGEX = /^\/challenge\/events\/[^/]+(?:\/watch)?/;

const SignInPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const { setUser } = useAuthStore();
  const { setUserProfile } = useStore();
  const auth = getAuth();

  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const isConnectRoute = location.pathname === "/challenge/connect";
  const [pairingCode, setPairingCode] = useState("");

  // --------- Pairing code extraction ----------
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const codeFromUrl = queryParams.get("code");
    if (codeFromUrl) {
      const upperCaseCode = codeFromUrl.toUpperCase();
      setPairingCode(upperCaseCode);
      console.log("Pairing code from URL:", upperCaseCode);
    }
  }, [location.search]);

  const handleCodeComplete = (completeCode) => setPairingCode(completeCode);

  // -------- Return-to logic (events only) --------
  // On arrival, stash a valid return target from router state or ?from=
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const fromQuery = qs.get("from");
    const stateFromPath =
      location.state?.from?.pathname || location.state?.from || null;

    const candidates = [stateFromPath, fromQuery];
    for (const c of candidates) {
      if (typeof c === "string" && EVENTS_PATH_REGEX.test(c)) {
        sessionStorage.setItem("returnToAfterAuth", c);
        break;
      }
    }
  }, [location.state, location.search]);

  const storedReturnTo = sessionStorage.getItem("returnToAfterAuth");

  // Default when nothing matches: /challenge/
  let from = "/challenge/";
  const stateFromPath =
    location.state?.from?.pathname || location.state?.from || null;
  const candidates = [stateFromPath, storedReturnTo];
  for (const c of candidates) {
    if (typeof c === "string" && EVENTS_PATH_REGEX.test(c)) {
      from = c;
      break;
    }
  }
  // ----------------------------------------------

  // Fetch user profile after auth
  const {
    userProfile,
    loading: profileLoading,
    error: profileError,
  } = useUserProfile(authenticatedUser ? authenticatedUser.uid : null);

  useEffect(() => {
    const redirectAfterProfileCheck = async () => {
      const updateUserProfileFromFirestore = async (user) => {
        try {
          const firestoreProfile = await fetchUserProfileFromFirestore(user.uid);
          if (firestoreProfile) {
            setUserProfile(firestoreProfile);
            return true;
          }
          return false;
        } catch (error) {
          console.error("Error fetching profile from Firestore:", error);
          return false;
        }
      };

      if (!profileLoading && authenticatedUser) {
        const firebaseProfileExists = await updateUserProfileFromFirestore(
          authenticatedUser
        );

        if (firebaseProfileExists || userProfile) {
          // Profile exists → go straight back to the event (if any)
          sessionStorage.removeItem("returnToAfterAuth");
          navigate(from, { replace: true });
        } else {
          // First-timer → send to Profile Edit, *carry the return path forward*
          navigate("/challenge/profile/edit", {
            replace: true,
            state: { from },
          });
        }
        setLoading(false);
      }

      if (profileError) {
        setError("Error fetching user profile.");
        setLoading(false);
      }
    };

    redirectAfterProfileCheck();
  }, [
    profileLoading,
    userProfile,
    profileError,
    authenticatedUser,
    setUserProfile,
    navigate,
    from,
  ]);

  // ---- Friendly auth errors ----
  const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/email-already-in-use":
        return "An account with this email already exists.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      default:
        return "An unexpected error occurred. Please try again later.";
    }
  };

  // ---- Third-party API flow helpers ----
  const postFormData = async (url, data) => {
    const response = await fetch(url, { method: "POST", body: data });
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    return await response.text();
  };

  const handleThirdPartyAPICalls = async (user) => {
    const token = await user.getIdToken();

    const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
    const data = new FormData();
    data.append("fbtoken", token);
    data.append("currentUser", JSON.stringify(user));

    try {
      const socialLoginResponse = await postFormData(socialLoginJWTURL, data);
      console.log("Social login JWT response:", socialLoginResponse);

      const socialAuthenticateURL =
        process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
      const authData = new FormData();
      authData.append("fbtoken", token);

      const authResponseData = await postFormData(
        socialAuthenticateURL,
        authData
      );
      console.log("Social authentication response:", authResponseData);

      localStorage.setItem("authResponseData", authResponseData);
      const authResponseJson = JSON.parse(authResponseData);
      const kbeanToken = authResponseJson.token;

      const customToken = await fetchCustomToken(user.uid);

      if (isConnectRoute) {
        try {
          const expirationTime = Timestamp.fromDate(
            new Date(Date.now() + 1 * 60 * 1000)
          );
          await setDoc(doc(db, "sessions", pairingCode), {
            userId: user.uid,
            email: user.email,
            token: customToken,
            kbeanToken,
            authenticated: true,
            expiration: expirationTime,
          });
          console.log("Session document created successfully.");
        } catch (error) {
          console.error("Failed to create session document:", error);
        }
      }
    } catch (apiError) {
      console.error(
        "Error authenticating. Please try again. ",
        apiError.message
      );
      setError("Failed to authenticate. Please try again.");
    }
  };

  const handlePostAuth = async (user) => {
    await handleThirdPartyAPICalls(user);
    setAuthenticatedUser(user);
  };

  // ---- Sign-in handlers ----
  const handleEmailPasswordSignIn = async (email, password) => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      setUser(user);
      await handlePostAuth(user);
    } catch (error) {
      setError(getFriendlyErrorMessage(error.code));
      console.error(
        "Error signing in with email and password:",
        error.message
      );
      setLoading(false);
    }
  };

  const handlePasswordReset = async (email) => {
    if (!email || !email.trim()) {
      setError("Please enter your email first to receive a reset link.");
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      await forgotPassword(email.trim());
      setNotice(
        "If an account exists for that email, we sent a password reset link."
      );
    } catch (err) {
      console.error("Password reset error:", err?.code || err?.message);
      setNotice(
        "If an account exists for that email, we sent a password reset link."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const googleProvider = new GoogleAuthProvider();
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      setUser(user);
      await handlePostAuth(user);
    } catch (error) {
      setError(getFriendlyErrorMessage(error.code));
      console.error("Error signing in with Google:", error.message);
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    const appleProvider = new OAuthProvider("apple.com");
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;
      setUser(user);
      await handlePostAuth(user);
    } catch (error) {
      setError(getFriendlyErrorMessage(error.code));
      console.error("Error signing in with Apple:", error.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Signing in...
          </Typography>
        </Box>
      </Backdrop>

      <AuthComponent
        theme={theme}
        pair={isConnectRoute}
        pairingCode={pairingCode}
        handleEmailPasswordSignIn={handleEmailPasswordSignIn}
        handleGoogleSignIn={handleGoogleSignIn}
        handleAppleSignIn={handleAppleSignIn}
        onCodeComplete={handleCodeComplete}
        onForgotPassword={handlePasswordReset}
        loading={loading}
        error={error}
        notice={notice}
      />
    </>
  );
};

export default SignInPage;
